import { useAuth } from '@clerk/clerk-react';
import { Download, RouteIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { API_BASE_URL } from '../api/client.js';
import { useMe } from '../api/useMe.js';
import { useTravelReport } from '../api/useReports.js';
import { useTeamsList } from '../api/useTeams.js';
import { EmptyState } from '../components/EmptyState.js';
import { cn } from '../lib/cn.js';

// Travel report page.
//
// Driving-distance summary per worker over a date range, optionally filtered
// by team. Reuses the journey monitoring data: rows reflect the 25 km/h
// driving floor and OSRM-matched distances when available.
//
// Authorisation is enforced server-side by requireCapability('monitor',
// 'admin', 'teamLeader'). A team-leader who only leads one team will see
// only that team's roster regardless of the filter UI here.

const DAY_MS = 24 * 60 * 60_000;

function toDateInputValue(d: Date): string {
  // YYYY-MM-DD in the user's local timezone — matches what <input type=date>
  // expects on every modern browser.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function dateToWindowIso(dateStr: string, endOfDay: boolean): string {
  // Treat the picker's value as a local-day boundary. Backend filters by
  // recorded_at which is timestamptz, so an ISO with the local offset works.
  const d = new Date(dateStr);
  if (endOfDay) d.setHours(23, 59, 59, 999);
  return d.toISOString();
}

export default function TravelReport() {
  const me = useMe();
  const teams = useTeamsList();
  const allTeams = teams.data?.teams ?? [];

  // Default window: last 7 days. Two date pickers so monitors can scope to
  // any range without a custom-range editor.
  const today = new Date();
  const weekAgo = new Date(today.getTime() - 7 * DAY_MS);
  const [fromDate, setFromDate] = useState(toDateInputValue(weekAgo));
  const [toDate, setToDate] = useState(toDateInputValue(today));
  const [teamId, setTeamId] = useState<string>('');

  const fromIso = useMemo(() => dateToWindowIso(fromDate, false), [fromDate]);
  const toIso = useMemo(() => dateToWindowIso(toDate, true), [toDate]);

  const report = useTravelReport(
    { from: fromIso, to: toIso, teamIds: teamId || undefined },
    Boolean(me.data),
  );

  const rows = report.data?.rows ?? [];
  const orgTotalsM = rows.reduce((acc, r) => acc + r.total_distance_m, 0);
  const orgTotalsCount = rows.reduce((acc, r) => acc + r.journey_count, 0);

  return (
    <div className="mx-auto max-w-7xl">
      <div className="mb-5 flex items-end justify-between">
        <div>
          <span className="eyebrow">Reports</span>
          <h1 className="mt-1 font-heading text-[22px] font-bold tracking-tightest text-ink">
            Travel
          </h1>
          <p className="text-sm text-ink-faint">
            Driving distance per worker. Walking inside a single client's home is excluded — only
            sessions averaging ≥ 25 km/h count. Distances are road-matched via OSRM when available;
            otherwise GPS polyline (marked ≈ on the Journeys page).
          </p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <label htmlFor="rt-team" className="mb-1 block text-[11px] font-medium text-ink-faint">
              Team
            </label>
            <select
              id="rt-team"
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
              className="min-w-[12rem] rounded-md border border-line-strong bg-white px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
            >
              <option value="">
                {me.data?.capabilities?.monitor || me.data?.capabilities?.admin
                  ? 'All teams'
                  : 'My teams'}
              </option>
              {allTeams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="rt-from" className="mb-1 block text-[11px] font-medium text-ink-faint">
              From
            </label>
            <input
              id="rt-from"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="rounded-md border border-line-strong bg-white px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
            />
          </div>
          <div>
            <label htmlFor="rt-to" className="mb-1 block text-[11px] font-medium text-ink-faint">
              To
            </label>
            <input
              id="rt-to"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="rounded-md border border-line-strong bg-white px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
            />
          </div>
          <CsvDownloadButton from={fromIso} to={toIso} teamIds={teamId || undefined} />
        </div>
      </div>

      {report.isError && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          Failed to load report: {(report.error as Error).message}
        </div>
      )}

      {report.isLoading && (
        <div className="panel p-8 text-center text-sm text-ink-faint">Loading report…</div>
      )}

      {!report.isLoading && rows.length === 0 && !report.isError && (
        <EmptyState
          icon={RouteIcon}
          title="No driving in this window"
          description="Nobody in scope drove far enough (≥ 25 km/h average) for any journey in the selected window. Try expanding the date range or picking a different team."
        />
      )}

      {rows.length > 0 && (
        <div className="panel">
          <div className="flex items-center justify-between border-b border-line px-4 py-3 text-xs">
            <span className="font-mono uppercase tracking-wider text-ink-faint">
              {rows.length} {rows.length === 1 ? 'worker' : 'workers'} · {orgTotalsCount} journeys
            </span>
            <span className="font-semibold text-ink">
              Total {(orgTotalsM / 1000).toFixed(1)} km
            </span>
          </div>
          <table className="min-w-full divide-y divide-line">
            <thead className="bg-surface-3">
              <tr>
                <Th>Worker</Th>
                <Th>Teams</Th>
                <Th className="text-right">Journeys</Th>
                <Th className="text-right">Total km</Th>
                <Th className="text-right">Avg km / journey</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line bg-white">
              {rows.map((r) => (
                <tr key={r.user_id} className="hover:bg-surface-3">
                  <Td>
                    <div className="text-sm font-medium text-ink">{r.display_name}</div>
                    {r.job_title && <div className="text-xs text-ink-faint">{r.job_title}</div>}
                  </Td>
                  <Td>
                    <div className="flex flex-wrap gap-1">
                      {r.team_names.length === 0 ? (
                        <span className="text-xs text-ink-mute">—</span>
                      ) : (
                        r.team_names.map((n) => (
                          <span
                            key={n}
                            className="rounded-full border border-line bg-surface-2 px-2 py-0.5 text-[10px] text-ink-dim"
                          >
                            {n}
                          </span>
                        ))
                      )}
                    </div>
                  </Td>
                  <Td className="text-right tabular-nums text-ink">{r.journey_count}</Td>
                  <Td className="text-right tabular-nums font-semibold text-ink">
                    {(r.total_distance_m / 1000).toFixed(1)}
                  </Td>
                  <Td className="text-right tabular-nums text-ink-dim">
                    {(r.avg_distance_per_journey_m / 1000).toFixed(1)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CsvDownloadButton({
  from,
  to,
  teamIds,
}: {
  from: string;
  to: string;
  teamIds?: string;
}) {
  const { getToken } = useAuth();
  const [busy, setBusy] = useState(false);

  async function onClick() {
    setBusy(true);
    try {
      const token = await getToken();
      const sp = new URLSearchParams({ from, to });
      if (teamIds) sp.set('team_ids', teamIds);
      const res = await fetch(`${API_BASE_URL}/reports/travel.csv?${sp.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `travel-report-${from.slice(0, 10)}-to-${to.slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className={cn(
        'inline-flex h-9 items-center gap-1.5 rounded-md border border-line-strong bg-white px-3 text-sm font-medium text-ink-dim transition hover:bg-surface-3 disabled:opacity-50',
      )}
      title="Download as CSV"
    >
      <Download className="h-3.5 w-3.5" />
      {busy ? 'Preparing…' : 'CSV'}
    </button>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={cn(
        'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-ink-faint',
        className,
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-4 py-3 text-sm', className)}>{children}</td>;
}
